/**
 * The owner-facing Notifications page's feed — same "no email-log table,
 * reconstruct from the `*_owner_email_sent_at` columns already loaded" idea
 * as buildOwnerEmailHistory (owner-email-history.ts), which the ADMIN's
 * owner detail view uses. Kept separate rather than shared: that one is
 * keyed by "sent vs. still pending" for an admin audit trail (English
 * titles, no icon, no id); this one is keyed by "what happened, in German,
 * with a stable id" for an owner's own inbox-style feed — different enough
 * shapes that forcing one function to serve both would mean threading a
 * mode flag through every push() call.
 *
 * Only ever includes an event once its mail has actually gone out (the
 * `*_owner_email_sent_at` column is set) — unlike the admin card, this feed
 * has no "pending" state to show, since an owner has no use for "an email
 * about to be sent to you."
 *
 * Pure function, no DB/network and no translator, so the ordering and the
 * events it includes are asserted in a test rather than discovered by
 * scrolling the real feed. What each one SAYS is owner-notification-copy.ts.
 */

export type OwnerNotificationKind =
  | "portal_ready"
  | "lease_signed"
  | "deposit_received"
  | "keys_handed_over"
  | "move_in_protocol"
  | "rent_received"
  | "move_out_date_confirmed"
  | "move_out_protocol"
  | "keys_returned";

/**
 * The few values a notification's copy varies by. Everything else about how it
 * reads lives in owner-notification-copy.ts.
 */
export interface OwnerNotificationParams {
  /** Tenant name, when the event has one. */
  tenant?: string | null;
  /** Room title, for the lease-signed line. */
  room?: string | null;
  /** EUR major units — a deposit or a rent payment. */
  amountEur?: number | null;
  /** An ISO date the sentence quotes, e.g. a confirmed move-out. */
  date?: string | null;
}

export interface OwnerNotificationEntry {
  /** Stable across reloads — `${kind}:${entityId}` — used as the read-state key. */
  id: string;
  kind: OwnerNotificationKind;
  /**
   * NO title or desc. They used to be built here as German sentences, which is
   * why the portal's EN switch translated everything around the feed and
   * nothing in it. ownerNotificationCopy() turns `kind` + `params` into words.
   */
  params: OwnerNotificationParams;
  /** The apartment/property label shown as the row's tag. */
  property: string;
  ts: string;
}

export interface OwnerNotificationApartment {
  id: number;
  label: string | null;
  portal_ready_owner_email_sent_at: string | null;
}

export interface OwnerNotificationBooking {
  id: number;
  /** Tenant name. */
  name: string | null;
  apartment_label: string | null;
  room_title: string | null;
  /** EUR major units (bookings.monthly_rent), not cents. */
  monthly_rent: number | null;
  /** EUR major units (bookings.deposit_amount), not cents. */
  deposit_amount: number | null;
  lease_signed_owner_email_sent_at: string | null;
  deposit_received_owner_email_sent_at: string | null;
  keys_handed_over_owner_email_sent_at: string | null;
  move_in_protocol_owner_email_sent_at: string | null;
  move_out_date: string | null;
  move_out_date_owner_email_sent_at: string | null;
  move_out_protocol_owner_email_sent_at: string | null;
  keys_returned_owner_email_sent_at: string | null;
}

export interface OwnerNotificationPayment {
  id: string;
  apartment_label: string | null;
  tenant_name: string | null;
  amount_cents: number;
  rent_received_owner_email_sent_at: string | null;
}

function euroDE(amountEur: number): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(amountEur);
}

function euroCentsDE(cents: number): string {
  return euroDE(cents / 100);
}

/** "2026-05-10" -> "10.05.2026". Passes through anything it cannot parse. */
function dateDE(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function buildOwnerNotificationFeed(input: {
  apartments: OwnerNotificationApartment[];
  bookings: OwnerNotificationBooking[];
  payments: OwnerNotificationPayment[];
}): OwnerNotificationEntry[] {
  const out: OwnerNotificationEntry[] = [];
  const push = (
    id: string,
    kind: OwnerNotificationKind,
    params: OwnerNotificationParams,
    property: string,
    ts: string
  ) => out.push({ id, kind, params, property, ts });

  for (const apt of input.apartments) {
    if (!apt.portal_ready_owner_email_sent_at) continue;
    const property = apt.label ?? "—";
    push(
      `portal_ready:${apt.id}`,
      "portal_ready",
      {},
      property,
      apt.portal_ready_owner_email_sent_at
    );
  }

  for (const b of input.bookings) {
    const property = b.apartment_label ?? "—";

    if (b.lease_signed_owner_email_sent_at) {
      push(
        `lease_signed:${b.id}`,
        "lease_signed",
        { tenant: b.name?.trim() || null, room: b.room_title },
        property,
        b.lease_signed_owner_email_sent_at
      );
    }
    if (b.deposit_received_owner_email_sent_at) {
      push(
        `deposit_received:${b.id}`,
        "deposit_received",
        { tenant: b.name?.trim() || null, amountEur: b.deposit_amount ?? 0 },
        property,
        b.deposit_received_owner_email_sent_at
      );
    }
    if (b.keys_handed_over_owner_email_sent_at) {
      push(
        `keys_handed_over:${b.id}`,
        "keys_handed_over",
        { tenant: b.name?.trim() || null },
        property,
        b.keys_handed_over_owner_email_sent_at
      );
    }
    if (b.move_in_protocol_owner_email_sent_at) {
      push(
        `move_in_protocol:${b.id}`,
        "move_in_protocol",
        {},
        property,
        b.move_in_protocol_owner_email_sent_at
      );
    }
    if (b.move_out_date_owner_email_sent_at) {
      push(
        `move_out_date_confirmed:${b.id}`,
        "move_out_date_confirmed",
        { tenant: b.name?.trim() || null, date: b.move_out_date },
        property,
        b.move_out_date_owner_email_sent_at
      );
    }
    if (b.move_out_protocol_owner_email_sent_at) {
      push(
        `move_out_protocol:${b.id}`,
        "move_out_protocol",
        {},
        property,
        b.move_out_protocol_owner_email_sent_at
      );
    }
    if (b.keys_returned_owner_email_sent_at) {
      push(
        `keys_returned:${b.id}`,
        "keys_returned",
        { tenant: b.name?.trim() || null },
        property,
        b.keys_returned_owner_email_sent_at
      );
    }
  }

  for (const p of input.payments) {
    if (!p.rent_received_owner_email_sent_at) continue;
    const property = p.apartment_label ?? "—";
    push(
      `rent_received:${p.id}`,
      "rent_received",
      {
        tenant: p.tenant_name?.trim() || null,
        amountEur: p.amount_cents / 100,
      },
      property,
      p.rent_received_owner_email_sent_at
    );
  }

  return out.sort(
    (a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime()
  );
}
